import { graphql } from 'react-relay';
import { JiraPriorityAri } from '@atlassian/ari/jira/priority';
import type { priorityIssueTransitionIssueAdjustmentsQuery as priorityIssueTransitionIssueAdjustmentsQueryType } from '@atlassian/jira-relay/src/__generated__/priorityIssueTransitionIssueAdjustmentsQuery.graphql';
import { FieldValueLookupError } from '../../../common/errors';

import type {
	PriorityPublicGetValue,
	PriorityPublicSetValue,
} from '../../../common/fields/priority';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import type {
	FieldOptionsFilter,
	OptionsVisibility,
} from '../../../common/types/options-visibility';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';

export type PriorityInternalValue = {
	label: string;
	value: string;
	iconUrl?: string;
	priorityId: string;
};

export const priorityGetValueTransformer = ({
	label,
	priorityId,
	iconUrl,
}: PriorityInternalValue): PriorityPublicGetValue => ({ name: label, id: priorityId, iconUrl });

// eslint-disable-next-line jira/relay/must-use-inline
export const priorityIssueTransitionIssueAdjustmentsQuery = graphql`
	query priorityIssueTransitionIssueAdjustmentsQuery(
		$ari: ID!
		$filterById: JiraFieldOptionIdsFilterInput
	) {
		node(id: $ari) {
			... on JiraPriorityField {
				# eslint-disable-next-line relay/unused-fields
				id
				# eslint-disable-next-line relay/unused-fields
				priorities(searchBy: "", filterById: $filterById) {
					edges {
						node {
							value: id
							label: name
							priorityId
							iconUrl
						}
					}
				}
			}
		}
	}
`;

export const prioritySetValueTransformer = async (
	inputValue: PriorityPublicSetValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<PriorityInternalValue | null> => {
	if (!inputValue) return null;
	const optionAri = JiraPriorityAri.create({
		priorityId: inputValue,
		siteId: tenantContext.cloudId,
		activationId: tenantContext.activationId,
	}).toString();

	const response: {
		edges?: [
			{
				node?: {
					value: string;
					label: string | null | undefined;
					iconUrl: string | null | undefined;
					priorityId: string;
				};
			},
		];
	} = await fetchFieldOptions<priorityIssueTransitionIssueAdjustmentsQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs: [optionAri],
		nodePropertyName: 'priorities',
		queryName: 'FETCH_PRIORITY',
		query: priorityIssueTransitionIssueAdjustmentsQuery,
	});

	const priority = response?.edges?.[0]?.node;

	if (priority?.value !== optionAri) {
		throw new FieldValueLookupError(inputValue);
	}

	return {
		value: priority.value,
		label: priority.label ?? '',
		iconUrl: priority.iconUrl ?? '',
		priorityId: priority.priorityId,
	};
};

export const transformSetOptionsVisibilityAriPriority = (
	{ isVisible, options }: OptionsVisibility,
	_lookupValues: undefined,
	{ tenantContext }: PublicToInternalPropertyTransformerContext,
): FieldOptionsFilter => ({
	operation: isVisible ? 'ALLOW' : 'EXCLUDE',
	optionIds: options.map((optionId) =>
		JiraPriorityAri.create({
			priorityId: optionId,
			siteId: tenantContext.cloudId,
			activationId: tenantContext.activationId,
		}).toString(),
	),
});
